// src/pages/messages/MessagesPage.tsx

import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  MessageCircle,
  RefreshCw,
  Router,
  Search,
  Send,
  Smartphone,
  Users,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface SmsTemplate {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  content: string;
  is_active?: boolean;
}

interface CustomerSummary {
  secret_id: string;
  customer_id?: string | null;
  username: string;
  customer_name?: string | null;
  account_number: string;
  phone_number: string;
  email: string | null;
  status: boolean;
  service_expires_at: string | null;
  amount_payable?: number | null;
  plan_id: string | null;
  plan_name: string | null;
  plan_price_cents: number | null;
  router_id: string | null;
  router_name: string | null;
  billing_account_id: string | null;
  billing_balance_cents: number | null;
  last_invoice_id: string | null;
  last_invoice_number: string | null;
  last_invoice_status: string | null;
  last_payment_id: string | null;
  last_payment_amount_cents: number | null;
  last_payment_paid_at: string | null;
  created_at: string;
}

interface CustomerStats {
  total: number;
  active: number;
  new_this_month: number;
}

interface RouterOption {
  id: string;
  name: string;
}

interface CustomersSummaryResponse {
  items: CustomerSummary[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  stats?: CustomerStats;
  routers?: RouterOption[];
}

interface SendSummary {
  total_targets: number;
  sent: number;
  failed: number;
  skipped: number;
}

interface SendResponse {
  sent?: unknown[];
  failed?: unknown[];
  skipped?: unknown[];
  summary?: SendSummary;
}

type Channel = "sms" | "whatsapp";
type RecipientType = "all" | "expired" | "specific";

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textareaCls =
  "w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const normalizeList = <T,>(res: unknown): T[] => {
  if (Array.isArray(res)) return res as T[];

  if (res && typeof res === "object") {
    const obj = res as {
      items?: T[];
      data?: T[];
      results?: T[];
    };

    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.results)) return obj.results;
  }

  return [];
};

const normalizeCustomersResponse = (
  res: unknown,
  fallbackPage: number,
  fallbackPageSize: number,
): CustomersSummaryResponse => {
  if (Array.isArray(res)) {
    return {
      items: res as CustomerSummary[],
      page: fallbackPage,
      page_size: fallbackPageSize,
      total: res.length,
      total_pages: 1,
      stats: {
        total: res.length,
        active: (res as CustomerSummary[]).filter((item) => item.status).length,
        new_this_month: 0,
      },
      routers: [],
    };
  }

  if (res && typeof res === "object") {
    const obj = res as Partial<CustomersSummaryResponse>;

    return {
      items: Array.isArray(obj.items) ? obj.items : [],
      page: Number(obj.page || fallbackPage),
      page_size: Number(obj.page_size || fallbackPageSize),
      total: Number(obj.total || 0),
      total_pages: Math.max(Number(obj.total_pages || 1), 1),
      stats: obj.stats,
      routers: Array.isArray(obj.routers) ? obj.routers : [],
    };
  }

  return {
    items: [],
    page: fallbackPage,
    page_size: fallbackPageSize,
    total: 0,
    total_pages: 1,
    stats: {
      total: 0,
      active: 0,
      new_this_month: 0,
    },
    routers: [],
  };
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatMoneyFromCents = (value?: number | null) => {
  if (value == null) return "KES 0.00";

  return `KES ${(value / 100).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatMoneyFromAmount = (value?: number | null) => {
  if (value == null) return "KES 0.00";

  return `KES ${Number(value || 0).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const calculateDaysLeft = (value?: string | null) => {
  if (!value) return "";

  const expiry = new Date(value);

  if (Number.isNaN(expiry.getTime())) return "";

  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return String(Math.max(diffDays, 0));
};

const getFirstName = (name?: string | null) => {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
};

const getCustomerAmount = (customer: CustomerSummary) => {
  if (customer.amount_payable != null) {
    return formatMoneyFromAmount(customer.amount_payable);
  }

  if (customer.billing_balance_cents != null) {
    return formatMoneyFromCents(customer.billing_balance_cents);
  }

  return formatMoneyFromCents(customer.plan_price_cents);
};

const customerDisplayName = (customer: CustomerSummary) =>
  customer.customer_name ||
  customer.username ||
  customer.account_number ||
  customer.phone_number ||
  "Customer";

const getInitials = (name: string) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .map((item) => item[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "C";

const StatusBadge: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${
      active
        ? "border-emerald-200 bg-emerald-100 text-emerald-800"
        : "border-slate-300 bg-slate-200 text-slate-600"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        active ? "bg-emerald-500" : "bg-slate-500"
      }`}
    />
    {active ? "Active" : "Inactive"}
  </span>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const ChannelButton: React.FC<{
  active: boolean;
  label: string;
  sub: string;
  icon: React.ReactNode;
  onClick: () => void;
  tone: "emerald" | "sky";
}> = ({ active, label, sub, icon, onClick, tone }) => {
  const activeCls =
    tone === "emerald"
      ? "border-emerald-300 bg-emerald-50 text-emerald-800 shadow-emerald-200/50"
      : "border-sky-300 bg-sky-50 text-sky-800 shadow-sky-200/50";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-3xl border p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 ${
        active
          ? activeCls
          : "border-slate-200 bg-slate-100 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
            active
              ? tone === "emerald"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-sky-100 text-sky-700"
              : "bg-slate-200 text-slate-600"
          }`}
        >
          {icon}
        </div>

        <div>
          <p className="text-[13.5px] font-black">{label}</p>
          <p className="mt-0.5 text-[11.5px] font-bold opacity-75">{sub}</p>
        </div>
      </div>
    </button>
  );
};

const RecipientOption: React.FC<{
  active: boolean;
  title: string;
  description: string;
  count: number;
  onClick: () => void;
}> = ({ active, title, description, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-3xl border p-4 text-left transition-all duration-200 ${
      active
        ? "border-emerald-300 bg-emerald-50 shadow-sm shadow-emerald-200/50"
        : "border-slate-200 bg-slate-100 hover:border-slate-300 hover:bg-slate-50"
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[13.5px] font-black text-slate-900">{title}</p>
        <p className="mt-1 text-[12px] font-medium leading-relaxed text-slate-500">
          {description}
        </p>
      </div>

      <span
        className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-black ${
          active
            ? "bg-emerald-100 text-emerald-800"
            : "bg-slate-200 text-slate-600"
        }`}
      >
        {count}
      </span>
    </div>
  </button>
);

const MessagesPage: React.FC = () => {
  const { showToast } = useToast();

  const [channel, setChannel] = useState<Channel>("sms");
  const [recipientType, setRecipientType] = useState<RecipientType>("expired");

  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [customMessage, setCustomMessage] = useState("");

  const [customers, setCustomers] = useState<CustomerSummary[]>([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPageSize, setCustomerPageSize] = useState(20);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customerTotalPages, setCustomerTotalPages] = useState(1);
  const [customerStats, setCustomerStats] = useState<CustomerStats>({
    total: 0,
    active: 0,
    new_this_month: 0,
  });
  const [routers, setRouters] = useState<RouterOption[]>([]);

  const [selectedCustomersById, setSelectedCustomersById] = useState<
    Record<string, CustomerSummary>
  >({});
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedCustomerIds = useMemo(
    () => Object.keys(selectedCustomersById),
    [selectedCustomersById],
  );

  const selectedCustomers = useMemo(
    () => Object.values(selectedCustomersById),
    [selectedCustomersById],
  );

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;

    return (
      templates.find((template) => template.id === selectedTemplateId) || null
    );
  }, [selectedTemplateId, templates]);

  const now = useMemo(() => new Date(), []);

  const expiredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      if (!customer.service_expires_at) return false;

      const expiryDate = new Date(customer.service_expires_at);

      if (Number.isNaN(expiryDate.getTime())) return false;

      return expiryDate < now;
    });
  }, [customers, now]);

  const totalCustomers =
    customerStats.total || customerTotal || customers.length;
  const activeCustomers = customerStats.active || 0;
  const expiredCountOnPage = expiredCustomers.length;

  const filteredByType = useMemo(() => {
    if (recipientType === "expired") return expiredCustomers;

    return customers;
  }, [recipientType, customers, expiredCustomers]);

  const displayedCustomers = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return filteredByType;

    return filteredByType.filter((customer) => {
      const username = String(customer.username || "").toLowerCase();
      const customerName = String(customer.customer_name || "").toLowerCase();
      const phone = String(customer.phone_number || "").toLowerCase();
      const planName = String(customer.plan_name || "").toLowerCase();
      const accountNumber = String(customer.account_number || "").toLowerCase();
      const routerName = String(customer.router_name || "").toLowerCase();

      return (
        username.includes(query) ||
        customerName.includes(query) ||
        phone.includes(query) ||
        planName.includes(query) ||
        accountNumber.includes(query) ||
        routerName.includes(query)
      );
    });
  }, [filteredByType, search]);

  const previewCustomer: CustomerSummary | null = useMemo(() => {
    if (recipientType === "specific" && selectedCustomers.length > 0) {
      return selectedCustomers[0];
    }

    return displayedCustomers[0] || customers[0] || null;
  }, [recipientType, selectedCustomers, displayedCustomers, customers]);

  const previewMessage = useMemo(() => {
    const base =
      selectedTemplate?.content ||
      customMessage ||
      "Your message preview will appear here…";

    if (!previewCustomer) return base;

    const amountDue = getCustomerAmount(previewCustomer);

    const mapping: Record<string, string> = {
      ":isp_name": "Your ISP",
      "{ISP_NAME}": "Your ISP",

      ":support_phone": "Support phone",
      ":support_email": "support@example.com",
      ":payment_method": "M-Pesa",
      ":paybill": "PayBill",
      ":business_number": "PayBill",
      ":payment_account": previewCustomer.account_number || "",

      ":first_name": getFirstName(customerDisplayName(previewCustomer)),
      ":full_name": customerDisplayName(previewCustomer),
      ":username": previewCustomer.username || "",
      ":phone": previewCustomer.phone_number || "",
      ":email": previewCustomer.email || "",

      ":account_number": previewCustomer.account_number || "",
      ":plan_name": previewCustomer.plan_name || "",
      ":amount_due": amountDue,

      ":expiry_date": formatDateTime(previewCustomer.service_expires_at),
      ":new_expiry_date": formatDateTime(previewCustomer.service_expires_at),
      ":days_left": calculateDaysLeft(previewCustomer.service_expires_at),

      ":paid_amount": formatMoneyFromCents(
        previewCustomer.last_payment_amount_cents,
      ),
      ":transaction_code": previewCustomer.last_invoice_number || "",
      ":paid_at": formatDateTime(previewCustomer.last_payment_paid_at),

      ":router_name": previewCustomer.router_name || "",
      ":service_location": "",
      ":maintenance_date": "",
      ":notice_message": "",
      ":pppoe_password": "",
      ":otp": "",
    };

    let rendered = base;

    Object.entries(mapping).forEach(([key, value]) => {
      rendered = rendered.replaceAll(key, value);
    });

    return rendered;
  }, [selectedTemplate, customMessage, previewCustomer]);

  const targetCount = useMemo(() => {
    if (recipientType === "specific") return selectedCustomerIds.length;
    if (recipientType === "all") return totalCustomers;

    return expiredCountOnPage;
  }, [
    recipientType,
    selectedCustomerIds.length,
    totalCustomers,
    expiredCountOnPage,
  ]);

  const loadTemplates = async () => {
    const templatesResponse = await fetchApp("/sms-templates/");
    setTemplates(normalizeList<SmsTemplate>(templatesResponse));
  };

  const loadCustomers = async (
    page = customerPage,
    pageSize = customerPageSize,
  ) => {
    const params = new URLSearchParams();

    params.set("page", String(page));
    params.set("page_size", String(pageSize));

    const customersResponse = await fetchApp(
      `/customers/summary?${params.toString()}`,
    );

    const normalized = normalizeCustomersResponse(
      customersResponse,
      page,
      pageSize,
    );

    setCustomers(normalized.items);
    setCustomerPage(normalized.page);
    setCustomerPageSize(normalized.page_size);
    setCustomerTotal(normalized.total);
    setCustomerTotalPages(normalized.total_pages);
    setCustomerStats(
      normalized.stats || {
        total: normalized.total,
        active: normalized.items.filter((item) => item.status).length,
        new_this_month: 0,
      },
    );
    setRouters(normalized.routers || []);
  };

  const loadData = async () => {
    try {
      if (templates.length === 0 && customers.length === 0) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      setError(null);

      await Promise.all([
        loadTemplates(),
        loadCustomers(customerPage, customerPageSize),
      ]);
    } catch (err: any) {
      const message = err?.message || "Failed to load templates and customers.";
      setError(message);
      showToast(message, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const reloadCustomerPage = async (page = customerPage) => {
    try {
      setCustomersLoading(true);
      setError(null);
      await loadCustomers(page, customerPageSize);
    } catch (err: any) {
      const message = err?.message || "Failed to load customers.";
      setError(message);
      showToast(message, "error");
    } finally {
      setCustomersLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading) return;
    reloadCustomerPage(customerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerPage, customerPageSize]);

  const toggleCustomer = (customer: CustomerSummary) => {
    if (recipientType !== "specific") return;

    setSelectedCustomersById((prev) => {
      const next = { ...prev };

      if (next[customer.secret_id]) {
        delete next[customer.secret_id];
      } else {
        next[customer.secret_id] = customer;
      }

      return next;
    });
  };

  const toggleDisplayedCustomers = () => {
    if (recipientType !== "specific") return;

    const allDisplayedSelected =
      displayedCustomers.length > 0 &&
      displayedCustomers.every(
        (customer) => selectedCustomersById[customer.secret_id],
      );

    if (allDisplayedSelected) {
      setSelectedCustomersById((prev) => {
        const next = { ...prev };

        displayedCustomers.forEach((customer) => {
          delete next[customer.secret_id];
        });

        return next;
      });

      return;
    }

    setSelectedCustomersById((prev) => {
      const next = { ...prev };

      displayedCustomers.forEach((customer) => {
        next[customer.secret_id] = customer;
      });

      return next;
    });
  };

  const handleTemplateChange = (value: string) => {
    setSelectedTemplateId(value);

    if (value) {
      setCustomMessage("");
    }
  };

  const handleMessageChange = (value: string) => {
    setCustomMessage(value);

    if (selectedTemplateId) {
      setSelectedTemplateId("");
    }
  };

  const handleSend = async () => {
    setError(null);
    setSuccess(null);

    if (!selectedTemplate && !customMessage.trim()) {
      setError("Please select a template or type a custom message.");
      return;
    }

    if (recipientType === "specific" && selectedCustomerIds.length === 0) {
      setError("Please select at least one customer in the table.");
      return;
    }

    try {
      setSending(true);

      const body = {
        channel,
        recipient_type: recipientType,
        secret_ids:
          recipientType === "specific" ? selectedCustomerIds : undefined,
        template_id: selectedTemplate ? selectedTemplate.id : undefined,
        custom_message: selectedTemplate ? undefined : customMessage.trim(),
      };

      const response = (await fetchApp("/messages/send", {
        method: "POST",
        body: JSON.stringify(body),
      })) as SendResponse;

      const summary = response?.summary;

      if (!summary) {
        setSuccess("Message request completed.");
        showToast("Message request completed.", "success");
        return;
      }

      if (summary.total_targets === 0) {
        const message =
          "No matching recipients were found. Check your recipient filter or selected customers.";
        setError(message);
        showToast(message, "error");
        return;
      }

      if (summary.failed > 0 || summary.skipped > 0) {
        const message = `Completed with issues. Sent: ${summary.sent}, Failed: ${summary.failed}, Skipped: ${summary.skipped}.`;
        setError(message);
        showToast(message, "error");
        return;
      }

      const message = `Messages sent successfully. Sent: ${summary.sent}.`;
      setSuccess(message);
      showToast(message, "success");
    } catch (err: any) {
      const message = err?.message || "Failed to send messages.";
      setError(message);
      showToast(message, "error");
    } finally {
      setSending(false);
    }
  };

  const allDisplayedSelected =
    recipientType === "specific" &&
    displayedCustomers.length > 0 &&
    displayedCustomers.every(
      (customer) => selectedCustomersById[customer.secret_id],
    );

  const canPrev = customerPage > 1;
  const canNext = customerPage < customerTotalPages;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto flex min-h-[55vh] max-w-[1600px] flex-col items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex h-14 w-14 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/40">
            <Loader2 className="h-7 w-7 animate-spin text-emerald-500" />
          </div>

          <p className="text-[13px] font-bold text-slate-500">
            Loading templates and customers…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Customer Communication
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Send Messages
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Notify customers through SMS or WhatsApp using saved
                  templates, custom text and targeted recipient groups.
                </p>
              </div>

              <button
                type="button"
                onClick={loadData}
                disabled={loading || sending || refreshing || customersLoading}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {(refreshing || customersLoading) && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            {customersLoading ? "Loading customer page…" : "Refreshing data…"}
          </div>
        )}

        {(error || success) && (
          <div
            className={`flex items-start justify-between gap-3 rounded-3xl border px-4 py-3.5 text-[13px] font-bold shadow-sm ${
              error
                ? "border-rose-200 bg-rose-50 text-rose-700 shadow-rose-200/40"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 shadow-emerald-200/40"
            }`}
          >
            <div className="flex items-start gap-3">
              {error ? (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <p>{error || success}</p>
            </div>

            <button
              type="button"
              onClick={() => {
                setError(null);
                setSuccess(null);
              }}
              className={`rounded-xl p-1.5 transition-colors ${
                error
                  ? "text-rose-600 hover:bg-rose-100"
                  : "text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:gap-4">
          <StatCard
            label="Total Customers"
            value={totalCustomers}
            sub="From paginated summary"
            icon={<Users className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Active Customers"
            value={activeCustomers}
            sub="Currently active"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Selected Targets"
            value={targetCount}
            sub={
              recipientType === "specific"
                ? "Manually selected"
                : recipientType === "all"
                  ? "All customers"
                  : "Expired on this page"
            }
            icon={<Send className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Templates"
            value={templates.length}
            sub="Saved SMS templates"
            icon={<FileText className="h-5 w-5" />}
            tone="sky"
          />
        </div>

        {/* Top Panels */}
        <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
          <section className="space-y-5">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
              <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
                <p className="text-[14px] font-black text-slate-900">
                  Message Setup
                </p>
                <p className="text-[11.5px] font-medium text-slate-500">
                  Choose the channel and recipient group before sending.
                </p>
              </div>

              <div className="space-y-5 p-4 sm:p-5">
                <div>
                  <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Channel
                  </p>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <ChannelButton
                      active={channel === "sms"}
                      label="SMS"
                      sub="Text message"
                      icon={<Smartphone className="h-5 w-5" />}
                      tone="sky"
                      onClick={() => setChannel("sms")}
                    />

                    <ChannelButton
                      active={channel === "whatsapp"}
                      label="WhatsApp"
                      sub="Chat message"
                      icon={<MessageCircle className="h-5 w-5" />}
                      tone="emerald"
                      onClick={() => setChannel("whatsapp")}
                    />
                  </div>

                  <p className="mt-2 text-[12px] font-medium text-slate-500">
                    SMS and WhatsApp are sent through TalkSasa.
                  </p>
                </div>

                <div>
                  <p className="mb-3 text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Recipients
                  </p>

                  <div className="space-y-3">
                    <RecipientOption
                      active={recipientType === "all"}
                      title="All customers"
                      description="Send to all customers with valid phone numbers."
                      count={totalCustomers}
                      onClick={() => setRecipientType("all")}
                    />

                    <RecipientOption
                      active={recipientType === "expired"}
                      title="Expired subscriptions"
                      description="Target customers whose service has expired. The table shows expired customers on the current page."
                      count={expiredCountOnPage}
                      onClick={() => setRecipientType("expired")}
                    />

                    <RecipientOption
                      active={recipientType === "specific"}
                      title="Specific customers"
                      description="Select individual customers from the paginated table below."
                      count={selectedCustomerIds.length}
                      onClick={() => setRecipientType("specific")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-5">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
              <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
                <p className="text-[14px] font-black text-slate-900">
                  Message Content
                </p>
                <p className="text-[11.5px] font-medium text-slate-500">
                  Pick a template or type a custom message.
                </p>
              </div>

              <div className="space-y-4 p-4 sm:p-5">
                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Template
                  </label>

                  <select
                    value={selectedTemplateId}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className={`${inputCls} cursor-pointer`}
                  >
                    <option value="">None — write custom message</option>

                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.code})
                      </option>
                    ))}
                  </select>

                  {selectedTemplate?.description && (
                    <p className="mt-2 text-[12px] font-medium text-slate-500">
                      {selectedTemplate.description}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400">
                    Message
                  </label>

                  <textarea
                    className={textareaCls}
                    rows={7}
                    placeholder="Dear :first_name, your :isp_name service for account :account_number..."
                    value={
                      selectedTemplate
                        ? selectedTemplate.content
                        : customMessage
                    }
                    onChange={(e) => handleMessageChange(e.target.value)}
                  />
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-100 px-4 py-3">
                  <p className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Available variables
                  </p>

                  <p className="mt-2 text-[11.5px] font-medium leading-relaxed text-slate-600">
                    :isp_name, :support_phone, :support_email, :payment_method,
                    :paybill, :payment_account, :first_name, :full_name,
                    :username, :phone, :email, :account_number, :plan_name,
                    :amount_due, :expiry_date, :new_expiry_date, :days_left,
                    :paid_amount, :transaction_code, :paid_at, :router_name,
                    :maintenance_date, :notice_message, :pppoe_password, :otp
                  </p>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-950 shadow-xl shadow-slate-400/25">
              <div className="border-b border-slate-800 bg-slate-900 px-4 py-4 sm:px-5">
                <p className="text-[14px] font-black text-slate-50">
                  Message Preview
                </p>

                {previewCustomer ? (
                  <p className="text-[11.5px] font-medium text-slate-400">
                    Example: {customerDisplayName(previewCustomer)} ·{" "}
                    {previewCustomer.phone_number}
                  </p>
                ) : (
                  <p className="text-[11.5px] font-medium text-slate-400">
                    Select a recipient or change the filter to preview customer
                    values.
                  </p>
                )}
              </div>

              <div className="p-4 sm:p-5">
                <div className="min-h-[120px] whitespace-pre-wrap rounded-3xl border border-slate-800 bg-slate-900/80 px-4 py-4 text-[13px] font-medium leading-relaxed text-slate-100">
                  {previewMessage}
                </div>

                <p className="mt-3 text-[11.5px] font-medium leading-relaxed text-slate-400">
                  Preview uses sample ISP values. Actual ISP name, PayBill and
                  support contact are filled from the logged-in client on the
                  backend.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Recipients List */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <Users className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Recipients List
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  Page {customerPage} of {customerTotalPages} · {customerTotal}{" "}
                  total customers
                  {recipientType === "specific" &&
                    ` · ${selectedCustomerIds.length} selected`}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full lg:w-96">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  placeholder="Search current page..."
                  className={`${inputCls} pl-10`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <select
                value={customerPageSize}
                onChange={(e) => {
                  setCustomerPage(1);
                  setCustomerPageSize(Number(e.target.value));
                }}
                className="h-10 rounded-2xl border border-slate-300 bg-slate-50 px-3 text-[12px] font-bold text-slate-600 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10"
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">
                    <input
                      type="checkbox"
                      disabled={
                        recipientType !== "specific" ||
                        displayedCustomers.length === 0
                      }
                      checked={allDisplayedSelected}
                      onChange={toggleDisplayedCustomers}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </th>
                  <th className="px-5 py-3.5 font-black">Customer</th>
                  <th className="px-5 py-3.5 font-black">Account</th>
                  <th className="px-5 py-3.5 font-black">Phone</th>
                  <th className="px-5 py-3.5 font-black">Plan</th>
                  <th className="px-5 py-3.5 font-black">Amount</th>
                  <th className="px-5 py-3.5 font-black">Router</th>
                  <th className="px-5 py-3.5 font-black">Service expiry</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {customersLoading ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center">
                      <div className="flex items-center justify-center gap-2 text-[13px] font-bold text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                        Loading customers…
                      </div>
                    </td>
                  </tr>
                ) : displayedCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Users className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No customers found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        Try changing the recipient filter, search term, or page.
                      </p>
                    </td>
                  </tr>
                ) : (
                  displayedCustomers.map((customer) => {
                    const isSelected = Boolean(
                      selectedCustomersById[customer.secret_id],
                    );

                    return (
                      <tr
                        key={customer.secret_id}
                        className={`transition-colors hover:bg-emerald-50/60 ${
                          recipientType === "specific" ? "cursor-pointer" : ""
                        }`}
                        onClick={() => toggleCustomer(customer)}
                      >
                        <td className="px-5 py-4">
                          <input
                            type="checkbox"
                            disabled={recipientType !== "specific"}
                            checked={isSelected}
                            onChange={() => toggleCustomer(customer)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[12px] font-black text-emerald-700 ring-1 ring-emerald-200">
                              {getInitials(customerDisplayName(customer))}
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-[13px] font-black text-slate-900">
                                {customerDisplayName(customer)}
                              </p>

                              <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                                {customer.email || "No email"}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4 font-mono text-[12px] font-bold text-slate-600">
                          {customer.account_number || "—"}
                        </td>

                        <td className="px-5 py-4 font-mono text-[12px] font-bold text-slate-600">
                          {customer.phone_number || "—"}
                        </td>

                        <td className="px-5 py-4">
                          {customer.plan_name ? (
                            <span className="inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
                              {customer.plan_name}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>

                        <td className="px-5 py-4 font-mono text-[12px] font-black text-slate-800">
                          {getCustomerAmount(customer)}
                        </td>

                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                            <Router className="h-3 w-3" />
                            {customer.router_name || "—"}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                          {formatDateTime(customer.service_expires_at)}
                        </td>

                        <td className="px-5 py-4">
                          <StatusBadge active={customer.status} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {customersLoading ? (
              <div className="px-4 py-12 text-center">
                <Loader2 className="mx-auto h-5 w-5 animate-spin text-emerald-500" />
                <p className="mt-2 text-[13px] font-bold text-slate-500">
                  Loading customers…
                </p>
              </div>
            ) : displayedCustomers.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Users className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No customers found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Try changing the recipient filter, search term, or page.
                </p>
              </div>
            ) : (
              displayedCustomers.map((customer) => {
                const isSelected = Boolean(
                  selectedCustomersById[customer.secret_id],
                );

                return (
                  <button
                    key={customer.secret_id}
                    type="button"
                    onClick={() => toggleCustomer(customer)}
                    disabled={recipientType !== "specific"}
                    className={`w-full px-4 py-4 text-left transition-colors ${
                      recipientType === "specific"
                        ? "hover:bg-emerald-50 active:bg-emerald-100"
                        : "cursor-default"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[12px] font-black text-emerald-700 ring-1 ring-emerald-200">
                          {getInitials(customerDisplayName(customer))}
                        </div>

                        <div className="min-w-0">
                          <p className="truncate text-[13.5px] font-black text-slate-900">
                            {customerDisplayName(customer)}
                          </p>

                          <p className="mt-0.5 truncate font-mono text-[11.5px] font-bold text-slate-500">
                            {customer.account_number || "No account"}
                          </p>

                          {customer.plan_name && (
                            <span className="mt-2 inline-flex rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[10.5px] font-black text-sky-800">
                              {customer.plan_name}
                            </span>
                          )}
                        </div>
                      </div>

                      <input
                        type="checkbox"
                        disabled={recipientType !== "specific"}
                        checked={isSelected}
                        onChange={() => toggleCustomer(customer)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                          Phone
                        </p>

                        <p className="mt-1 font-mono text-[12px] font-bold text-slate-700">
                          {customer.phone_number || "—"}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                          Amount
                        </p>

                        <p className="mt-1 font-mono text-[12px] font-black text-slate-900">
                          {getCustomerAmount(customer)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Expiry
                      </p>

                      <p className="mt-1 text-[12px] font-bold text-slate-700">
                        {formatDateTime(customer.service_expires_at)}
                      </p>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                        <Router className="h-3 w-3" />
                        {customer.router_name || "No router"}
                      </span>

                      <StatusBadge active={customer.status} />
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Pagination + Send */}
          <div className="flex flex-col gap-3 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <p className="text-[12px] font-medium text-slate-500">
                Page{" "}
                <span className="font-black text-slate-800">
                  {customerPage}
                </span>{" "}
                of{" "}
                <span className="font-black text-slate-800">
                  {customerTotalPages}
                </span>
                <span className="mx-2 text-slate-300">·</span>
                <span className="font-black text-slate-800">
                  {customerTotal}
                </span>{" "}
                customers
                {recipientType === "specific" && (
                  <>
                    <span className="mx-2 text-slate-300">·</span>
                    <span className="font-black text-slate-800">
                      {selectedCustomerIds.length}
                    </span>{" "}
                    selected across pages
                  </>
                )}
              </p>

              {routers.length > 0 && (
                <p className="text-[12px] font-medium text-slate-500">
                  Routers:{" "}
                  <span className="font-black text-slate-800">
                    {routers.map((router) => router.name).join(", ")}
                  </span>
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
                <button
                  type="button"
                  disabled={!canPrev || customersLoading}
                  onClick={() =>
                    setCustomerPage((current) => Math.max(current - 1, 1))
                  }
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </button>

                <button
                  type="button"
                  disabled={!canNext || customersLoading}
                  onClick={() =>
                    setCustomerPage((current) =>
                      Math.min(current + 1, customerTotalPages),
                    )
                  }
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                className={`${btnBase} h-11 bg-gradient-to-r from-emerald-500 to-teal-500 px-6 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {sending ? "Sending…" : `Send to ${targetCount}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
